import { X } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { translate } from '@/lib/i18n/translate';
import { cn } from '@/lib/utils';

type ToastTone = 'neutral' | 'error';

interface ToastAction {
  label: string;
  run: () => void;
}

interface Toast {
  id: number;
  message: string;
  tone: ToastTone;
  action?: ToastAction;
}

interface ToastApi {
  show: (message: string, tone?: ToastTone) => void;
  /** Used by optimistic mutations when a rollback happens. */
  error: (message: string) => void;
  /**
   * A message with a way to take it back.
   *
   * Ticking a task makes it disappear from the list, which is the right
   * behaviour and also means a mis-click leaves you staring at a gap wondering
   * what used to be there. This says what went, and offers it back — cheaper
   * than a confirmation dialog on every completion, which would tax the
   * common case to protect against the rare one.
   */
  undo: (message: string, run: () => void) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

let nextId = 1;
const VISIBLE_MS = 4000;
//: Long enough to notice a mis-click and act on it.
const UNDO_MS = 9000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (message: string, tone: ToastTone, action?: ToastAction, lifetime = VISIBLE_MS) => {
      const id = nextId++;
      setToasts((current) => [
        ...current,
        { id, message, tone, ...(action ? { action } : {}) },
      ]);
      window.setTimeout(() => dismiss(id), lifetime);
      return id;
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      show: (message: string, tone: ToastTone = 'neutral') => {
        push(message, tone);
      },
      error: (message: string) => {
        push(message, 'error');
      },
      undo: (message: string, run: () => void) => {
        // Longer than a plain notice: realising you ticked the wrong thing
        // takes a moment, and the whole point is to still be able to act on it.
        let toastId = -1;
        toastId = push(
          message,
          'neutral',
          {
            label: translate('common.undo'),
            run: () => {
              run();
              dismiss(toastId);
            },
          },
          UNDO_MS,
        );
      },
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-[100] flex flex-col gap-2"
        // Announced without stealing focus — a rollback message must not
        // interrupt whatever the user is typing next.
        role="status"
        aria-live="polite"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={cn(
              'pointer-events-auto flex max-w-md items-center gap-3 rounded-md border',
              'px-3.5 py-2.5 text-sm shadow-lg shadow-black/10',
              toast.tone === 'error'
                ? 'border-overdue-border bg-overdue-subtle text-overdue'
                : 'border-border bg-surface-raised text-content',
            )}
          >
            <span className="min-w-0 flex-1 truncate">{toast.message}</span>
            {toast.action ? (
              <button
                type="button"
                onClick={toast.action.run}
                className="shrink-0 font-medium text-accent underline underline-offset-2 hover:text-accent-hover"
              >
                {toast.action.label}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => dismiss(toast.id)}
              aria-label={translate('common.close')}
              className="shrink-0 opacity-50 transition-surface hover:opacity-100"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside <ToastProvider>');
  return context;
}
